import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  Calendar,
  AlertCircle,
  Search,
  Filter,
  Grid,
  List,
  Lightbulb,
  Clock,
  Clock3,
  Repeat,
  Target,
  ClipboardList,
  CheckCircle2,
  Zap,
  Layers,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Award,
  X,
  Sparkles,
} from "lucide-react";
import "./DiscoverMoreTest.scss";
import axiosInstance from "../../axiosInstace";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import IconButton from "../../components/IconButton/IconButton";
import { useAuth } from "../../contexts/AuthContext";
import TestResults from "../TestResults/TestResults";
import { toast } from "react-toastify";

// Quick tips shown in the sidebar. Static copy — tweak freely.
const QUICK_TIPS = [
  {
    icon: Clock,
    text: "Set aside a quiet 10–20 minutes so you can finish in one sitting.",
  },
  {
    icon: Target,
    text: "Answer honestly rather than how you think you 'should' — there's no right answer.",
  },
  {
    icon: AlertCircle,
    text: "One-time assessments can't be retaken, so pace yourself before you start.",
  },
  {
    icon: Repeat,
    text: "Unlimited assessments can be revisited any time to track how you change.",
  },
];

// Matches com.hrlabs.ourdept.domain.public_tool.enums.selfdiscovery.TestAttemptType
const ATTEMPT_TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "ONE_TIME", label: "One-Time" },
  { value: "MULTIPLE", label: "Multiple Attempts" },
  { value: "UNLIMITED", label: "Unlimited" },
];

const DURATION_OPTIONS = [
  { value: "all", label: "All Durations" },
  { value: "under10", label: "Under 10 min" },
  { value: "10to20", label: "10–20 min" },
  { value: "over20", label: "Over 20 min" },
  { value: "none", label: "No duration set" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "available", label: "Available Only" },
  { value: "expired", label: "Expired Tests" },
];

const formatAttemptDate = (isoString) => {
  if (!isoString) return "Completion date unavailable";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "Completion date unavailable";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Namespaces per-user UI preferences (sidebar/quick-tips collapsed state)
// so one browser profile shared by multiple accounts doesn't bleed
// settings between them. Swap the lookup below for however this app
// actually stores the current user (auth context, redux, etc.) if it
// differs.
const getUserScopedStorageKey = (suffix) => {
  try {
    const rawUser =
      localStorage.getItem("user") || localStorage.getItem("userInfo");
    const parsedUser = rawUser ? JSON.parse(rawUser) : null;
    const userId =
      parsedUser?.id || parsedUser?.userId || parsedUser?._id || "guest";
    return `discover_${suffix}_${userId}`;
  } catch {
    return `discover_${suffix}_guest`;
  }
};

// Maps the API's testAttemptType to a small metadata object used to
// color-code cards/icons and label the attempt style consistently.
const getAttemptTypeMeta = (test) => {
  switch (test.testAttemptType) {
    case "ONE_TIME":
      return { key: "onetime", label: "One-Time Only" };
    case "UNLIMITED":
      return { key: "unlimited", label: "Unlimited Attempts" };
    case "MULTIPLE":
      return { key: "multiple", label: `${test.maxAttempts} Attempts` };
    default:
      return { key: "default", label: "Assessment" };
  }
};

// Parses simple ISO-8601 durations like "PT10M" / "PT1H30M" into minutes.
const parseDurationMinutes = (duration) => {
  if (!duration) return null;
  if (typeof duration === "object" && typeof duration.seconds === "number") {
    const minutes = Math.round(duration.seconds / 60);
    return minutes > 0 ? minutes : null;
  }
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?$/.exec(duration);
  if (!match) return null;
  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const total = hours * 60 + minutes;
  return total > 0 ? total : null;
};

// Extracts a comparable value for each sortable table column. Missing
// values are pushed to one consistent end (no expiry sorts last, no
// duration sorts lowest) rather than throwing off the comparator with
// nulls/undefined.
const getSortValue = (test, key, statusResolver) => {
  switch (key) {
    case "title":
      return (test.testTitle || "").toLowerCase();
    case "status":
      return statusResolver(test).class === "available" ? 1 : 0;
    case "expires":
      return test.linkExpiryDate
        ? new Date(test.linkExpiryDate).getTime()
        : Infinity;
    case "duration":
      return parseDurationMinutes(test.testDuration) ?? -1;
    default:
      return 0;
  }
};

const DiscoverMoreTests = () => {
  const navigate = useNavigate();
  const { isCreator, user } = useAuth();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "all" = merged view of both endpoints below (deduped by testId)
  // "own" = every published test (GET /tests/getall)
  // "assigned" = only tests assigned to the logged-in user (GET /tests/assigned-tests)
  const [activeTab, setActiveTab] = useState("all");
  const [assignedTests, setAssignedTests] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(true);
  const [assignedError, setAssignedError] = useState(null);
  const [exploreTests, setExploreTests] = useState([]);
  const [exploreLoading, setExploreLoading] = useState(false);
  const [exploreError, setExploreError] = useState(null);
  const [exploreFetched, setExploreFetched] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterDuration, setFilterDuration] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [completedTestIds, setCompletedTestIds] = useState(new Set());
  const [resultLoadingTestId, setResultLoadingTestId] = useState(null);
  const [selectedTestResults, setSelectedTestResults] = useState(null);
  const [resultAttemptsPopup, setResultAttemptsPopup] = useState(null);

  const [tipsCollapsed, setTipsCollapsed] = useState(() => {
    try {
      return (
        sessionStorage.getItem(
          getUserScopedStorageKey("quick_tips_collapsed"),
        ) === "true"
      );
    } catch {
      return false;
    }
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return (
        sessionStorage.getItem(getUserScopedStorageKey("sidebar_collapsed")) ===
        "true"
      );
    } catch {
      return false;
    }
  });

  useEffect(() => {
    fetchAllTests();
    fetchAssignedTests();
  }, []);

  useEffect(() => {
    const fetchCompletedTests = async () => {
      if (!user?.userDetailsId) return;

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/tests/results/${user.userDetailsId}`,
        );
        const results = response.data?.data?.testResults || [];
        setCompletedTestIds(
          new Set(results.map((result) => String(result.testId))),
        );
      } catch (err) {
        // A 201 means no history yet; Discover remains usable either way.
        if (err.response?.status !== 201) {
          console.error("Failed to load completed assessment history:", err);
        }
        setCompletedTestIds(new Set());
      }
    };

    fetchCompletedTests();
  }, [user?.userDetailsId]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        getUserScopedStorageKey("quick_tips_collapsed"),
        String(tipsCollapsed),
      );
    } catch {
      // sessionStorage unavailable (private mode, etc.) — safe to ignore
    }
  }, [tipsCollapsed]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        getUserScopedStorageKey("sidebar_collapsed"),
        String(sidebarCollapsed),
      );
    } catch {
      // sessionStorage unavailable (private mode, etc.) — safe to ignore
    }
  }, [sidebarCollapsed]);

  const fetchAllTests = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/getall`,
      );
      if (response.data && response.data.data) {
        setTests(response.data.data);
      }
    } catch (err) {
      setError("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedTests = async () => {
    setAssignedLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/assigned-tests`,
      );
      if (response.data && response.data.data) {
        setAssignedTests(response.data.data);
      }
      setAssignedError(null);
    } catch (err) {
      setAssignedError("Failed to load assigned assessments");
    } finally {
      setAssignedLoading(false);
    }
  };

  const fetchExploreTests = async () => {
    setExploreLoading(true);
    setExploreError(null);
    try {
      const response = await axiosInstance.get("/api/user/tests/templates");
      const adminTests = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      // The admin endpoint serializes its Duration field as `testduration`.
      // Normalising it keeps the existing Discover cards and filters reusable.
      setExploreTests(
        adminTests.map((test) => ({
          ...test,
          testDuration: test.testDuration ?? test.testduration ?? null,
          usedAttempts: test.usedAttempts ?? 0,
          remainingAttempts: test.remainingAttempts ?? null,
        })),
      );
      setExploreFetched(true);
    } catch (err) {
      console.error("Failed to load admin-created assessments:", err);
      setExploreError("Failed to load featured assessments");
    } finally {
      setExploreLoading(false);
    }
  };

  const handleExploreTab = () => {
    setActiveTab("explore");
    if (!exploreFetched && !exploreLoading) fetchExploreTests();
  };

  const handleStartTest = (testId) => {
    navigate(`/test/${testId}`);
  };

  const handleViewResults = async (test) => {
    if (!user?.userDetailsId || resultLoadingTestId) return;

    setResultLoadingTestId(test.testId);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/users/${user.userDetailsId}/tests/${test.testId}/attempts`,
      );
      const attempts = response.data?.data || [];

      if (attempts.length === 0) {
        toast.info("No completed result is available for this assessment yet.");
        return;
      }

      const sortedAttempts = [...attempts].sort((a, b) => {
        const attemptOrder = (b.attemptNumber || 0) - (a.attemptNumber || 0);
        if (attemptOrder !== 0) return attemptOrder;
        return new Date(b.endTime || 0) - new Date(a.endTime || 0);
      });
      setResultAttemptsPopup({ test, attempts: sortedAttempts });
    } catch (err) {
      console.error("Failed to load assessment results:", err);
      toast.error("Couldn't load the assessment results. Please try again.");
    } finally {
      setResultLoadingTestId(null);
    }
  };

  const openAttemptResults = (test, attempt) => {
    const results = {
      testId: test.testId,
      totalScore: attempt.totalScore,
      totalTestScore: attempt.totalTestScore ?? null,
      interpretationTitle: attempt.interpretation?.title || null,
      interpretationDescription: attempt.interpretation?.description || null,
      minInterpretationValue: attempt.interpretation?.minScore ?? null,
      maxInterpretationValue: attempt.interpretation?.maxScore ?? null,
      recommendationTitle: attempt.recommendation?.title || null,
      recommendationDescription:
        attempt.recommendation?.recommendationText || null,
      minRecommendationValue: attempt.recommendation?.minScore ?? null,
      maxRecommendationValue: attempt.recommendation?.maxScore ?? null,
      sections: (attempt.sections || []).map((section, index) => ({
        sectionId: section.sectionId ?? index,
        sectionTitle: section.sectionTitle,
        sectionScore: section.sectionScore,
        totalSectionScore: section.totalSectionScore ?? null,
        sectionInterpretationTitle: section.sectionInterpretationTitle || null,
        sectionInterpretationDescription:
          section.sectionInterpretationDescription || null,
        sectionRecommendationTitle: section.sectionRecommendationTitle || null,
        sectionRecommendationDescription:
          section.sectionRecommendationDescription ||
          section.sectionRecommendationText ||
          null,
      })),
    };

    setResultAttemptsPopup(null);
    setSelectedTestResults({
      results,
      testData: { testId: test.testId, testTitle: test.testTitle },
    });
  };

  const handleCreateTest = () => {
    navigate("/test-management");
  };

  const getStatusBadge = (test) => {
    const now = new Date();
    const expiryDate = test.linkExpiryDate
      ? new Date(test.linkExpiryDate)
      : null;
    if (expiryDate && expiryDate < now) {
      return { label: "Expired", class: "expired" };
    }
    return { label: "Available", class: "available" };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No expiry";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const canStartTest = (test) => {
    if (getStatusBadge(test).class === "expired") return false;
    return test.canAttempt === true;
  };

  const renderAttemptDetails = (test) => {
    if (test.testAttemptType === "UNLIMITED") {
      return (
        <div className="meta-item attempt-info unlimited">
          <span className="attempt-badge unlimited">∞ Unlimited attempts</span>
        </div>
      );
    }

    if (test.testAttemptType === null) return null;

    return (
      <div className="meta-item attempt-info">
        <div className="attempt-counters">
          <span className="attempt-used">
            <strong>{test.usedAttempts ?? 0}</strong>{" "}
            {test.usedAttempts === 1 ? "attempt" : "attempts"} used
          </span>
          {test.remainingAttempts !== null && (
            <>
              <span className="attempt-divider">·</span>
              <span
                className={`attempt-remaining ${
                  test.remainingAttempts === 0 ? "none-left" : ""
                }`}
              >
                <strong>{test.remainingAttempts}</strong> remaining
              </span>
            </>
          )}
        </div>
        {!test.canAttempt && (
          <span className="attempt-badge exhausted">No attempts left</span>
        )}
      </div>
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterType("all");
    setFilterDuration("all");
  };

  const hasActiveFilters =
    searchTerm !== "" ||
    filterStatus !== "all" ||
    filterType !== "all" ||
    filterDuration !== "all";

  // Merged, deduped view for the "all" tab — union of both endpoints,
  // keyed by testId so a test that appears in both the full catalog and
  // the user's assigned list only shows up once.
  const mergedTests = useMemo(() => {
    const byId = new Map();
    tests.forEach((t) => byId.set(t.testId, t));
    assignedTests.forEach((t) => {
      if (!byId.has(t.testId)) byId.set(t.testId, t);
    });
    return Array.from(byId.values());
  }, [tests, assignedTests]);

  // Source list depends on which tab is active
  const activeTests =
    activeTab === "assigned"
      ? assignedTests
      : activeTab === "own"
        ? tests
        : activeTab === "explore"
          ? exploreTests
          : mergedTests;

  const filteredTests = activeTests.filter((test) => {
    // Some API records have a null title. Treat missing searchable fields as
    // empty strings so one incomplete assessment cannot crash Discover.
    const query = searchTerm.toLowerCase();
    const matchesSearch =
      String(test?.testTitle ?? "")
        .toLowerCase()
        .includes(query) ||
      String(test?.description ?? "")
        .toLowerCase()
        .includes(query);
    if (!matchesSearch) return false;

    if (filterStatus !== "all") {
      const status = getStatusBadge(test);
      if (status.class !== filterStatus) return false;
    }

    if (filterType !== "all" && test.testAttemptType !== filterType) {
      return false;
    }

    if (filterDuration !== "all") {
      const minutes = parseDurationMinutes(test.testDuration);
      if (filterDuration === "none" && minutes !== null) return false;
      if (filterDuration === "under10" && !(minutes !== null && minutes < 10))
        return false;
      if (
        filterDuration === "10to20" &&
        !(minutes !== null && minutes >= 10 && minutes <= 20)
      )
        return false;
      if (filterDuration === "over20" && !(minutes !== null && minutes > 20))
        return false;
    }

    return true;
  });

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="sort-icon" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={12} className="sort-icon active" />
    ) : (
      <ArrowDown size={12} className="sort-icon active" />
    );
  };

  const sortedTests = useMemo(() => {
    if (!sortConfig.key) return filteredTests;
    return [...filteredTests].sort((a, b) => {
      const valueA = getSortValue(a, sortConfig.key, getStatusBadge);
      const valueB = getSortValue(b, sortConfig.key, getStatusBadge);
      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTests, sortConfig]);

  // Small stat pills shown inline in the header. Computed directly from
  // `tests` (rather than calling canStartTest/getStatusBadge) so the
  // dependency array below stays accurate and lint-clean.
  const stats = useMemo(() => {
    const now = new Date();
    const activeCount = activeTests.filter((t) => {
      const expiryDate = t.linkExpiryDate ? new Date(t.linkExpiryDate) : null;
      const isExpired = expiryDate && expiryDate < now;
      return !isExpired && t.canAttempt === true;
    }).length;
    const startedCount = activeTests.filter(
      (t) => (t.usedAttempts ?? 0) > 0,
    ).length;
    const completionRate =
      activeTests.length > 0
        ? Math.round((startedCount / activeTests.length) * 100)
        : 0;

    return {
      total: activeTests.length,
      active: activeCount,
      completionRate,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTests]);

  // "own"/"assigned" map 1:1 to their endpoint's own loading/error state.
  // "all" depends on both — keep showing data as soon as either resolves,
  // and only surface an error screen if both endpoints failed (a single
  // failure still leaves a usable partial list in `mergedTests`).
  const isLoading =
    activeTab === "assigned"
      ? assignedLoading
      : activeTab === "own"
        ? loading
        : activeTab === "explore"
          ? exploreLoading
          : loading || assignedLoading;
  const currentError =
    activeTab === "assigned"
      ? assignedError
      : activeTab === "own"
        ? error
        : activeTab === "explore"
          ? exploreError
          : error && assignedError
            ? "Failed to load assessments"
            : null;
  const retryFetch =
    activeTab === "assigned"
      ? fetchAssignedTests
      : activeTab === "own"
        ? fetchAllTests
        : activeTab === "explore"
          ? fetchExploreTests
          : () => {
              fetchAllTests();
              fetchAssignedTests();
            };

  if (selectedTestResults) {
    return (
      <div className="full-screen-results">
        <TestResults
          results={selectedTestResults.results}
          hasAlreadyTaken={true}
          onRetakeTest={() =>
            handleStartTest(selectedTestResults.testData.testId)
          }
          onBackToDashboard={() => setSelectedTestResults(null)}
          testData={selectedTestResults.testData}
          isFromHistory={true}
        />
      </div>
    );
  }

  if (isLoading) return <LoadingScreen />;

  if (currentError) {
    return (
      <div className="discovery-more-tests">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Failed to Load Assessments</h2>
          <p>{currentError}</p>
          <ButtonWithIcon
            icon="material-symbols:refresh"
            text="Try Again"
            onClick={retryFetch}
            className="retry-btn"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="discovery-more-tests">
      {/* Header with inline stats */}
      <div className="discovery-header">
        <div className="header-content">
          <h1>Discover Assessments</h1>
          <p>
            Explore our complete collection of personality and skills
            assessments
          </p>
        </div>

        <div className="header-stats">
          <div className="mini-stat mini-stat--total">
            <div className="mini-stat-icon">
              <ClipboardList size={18} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">{stats.total}</span>
              <span className="mini-stat-label">Total Assessments</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--active">
            <div className="mini-stat-icon">
              <Zap size={18} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">{stats.active}</span>
              <span className="mini-stat-label">Active Tests</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--completion">
            <div className="mini-stat-icon">
              <CheckCircle2 size={18} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">{stats.completionRate}%</span>
              <span className="mini-stat-label">Completion Rate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + Filters/Search share one row on desktop, stack on mobile */}
      <div className="discovery-toolbar">
        <div className="discovery-tabs">
          <button
            type="button"
            className={`discovery-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All Tests
            <span className="discovery-tab__count">{mergedTests.length}</span>
          </button>
          {isCreator && (
            <button
              type="button"
              className={`discovery-tab ${activeTab === "own" ? "active" : ""}`}
              onClick={() => setActiveTab("own")}
            >
              Own Tests
              <span className="discovery-tab__count">{tests.length}</span>
            </button>
          )}
          <button
            type="button"
            className={`discovery-tab ${
              activeTab === "assigned" ? "active" : ""
            }`}
            onClick={() => setActiveTab("assigned")}
          >
            Assigned to Me
            <span className="discovery-tab__count">{assignedTests.length}</span>
          </button>
          <button
            type="button"
            className={`discovery-tab discovery-tab--explore ${
              activeTab === "explore" ? "active" : ""
            }`}
            onClick={handleExploreTab}
          >
            <Sparkles size={16} aria-hidden="true" />
            Explore
          </button>
        </div>

        {/* Filters and Search */}
        <div className="discovery-controls">
          <div className="search-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search assessments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-controls">
            <div className="filter-dropdown">
              <Layers size={14} />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                {ATTEMPT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-dropdown">
              <Clock3 size={14} />
              <select
                value={filterDuration}
                onChange={(e) => setFilterDuration(e.target.value)}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-dropdown">
              <Filter size={14} />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                <RotateCcw size={12} />
                Clear
              </button>
            )}

            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Card view"
              >
                <Grid size={14} />
              </button>
              <button
                className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="List view"
              >
                <List size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content + sidebar */}
      <div
        className={`discovery-layout ${
          sidebarCollapsed ? "sidebar-collapsed" : ""
        }`}
      >
        <div className="discovery-main">
          {/* Tests Grid / Table */}
          <div className={`tests-container ${viewMode}`}>
            {sortedTests.length > 0 ? (
              viewMode === "table" ? (
                /* ── TABLE / LIST VIEW ── */
                <table className="assessment-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="sort-btn"
                          onClick={() => toggleSort("title")}
                        >
                          Assessment
                          {renderSortIcon("title")}
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="sort-btn"
                          onClick={() => toggleSort("status")}
                        >
                          Status
                          {renderSortIcon("status")}
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="sort-btn"
                          onClick={() => toggleSort("expires")}
                        >
                          Expires
                          {renderSortIcon("expires")}
                        </button>
                      </th>
                      <th>Attempts</th>
                      <th className="assessment-table__action-header">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTests.map((test) => {
                      const status = getStatusBadge(test);
                      const startable = canStartTest(test);
                      const typeMeta = getAttemptTypeMeta(test);
                      const hasResults = completedTestIds.has(
                        String(test.testId),
                      );

                      return (
                        <tr
                          key={test.testId}
                          className={
                            status.class === "expired"
                              ? "row-expired"
                              : !startable
                                ? "row-exhausted"
                                : ""
                          }
                        >
                          {/* Title */}
                          <td>
                            <div className="assessment-table__title-cell">
                              <div
                                className={`assessment-table__icon assessment-table__icon--${typeMeta.key}`}
                              >
                                <Brain size={15} />
                              </div>
                              <div className="assessment-table__title-content">
                                <h4>{test.testTitle}</h4>
                                <p>
                                  {test.description ||
                                    "Comprehensive personality and skills assessment."}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Status */}
                          <td>
                            <span className={`status-badge ${status.class}`}>
                              {status.label}
                            </span>
                          </td>

                          {/* Expiry */}
                          <td data-label="Expires">
                            <div className="assessment-table__date">
                              <Calendar size={12} />
                              {formatDate(test.linkExpiryDate)}
                            </div>
                          </td>

                          {/* Attempts */}
                          <td>
                            {test.testAttemptType === "UNLIMITED" ? (
                              <span className="attempt-badge unlimited">
                                ∞ Unlimited
                              </span>
                            ) : test.testAttemptType === null ? (
                              <span className="assessment-table__na">—</span>
                            ) : (
                              <div className="assessment-table__attempts-wrap">
                                <span className="assessment-table__attempt-counts">
                                  <span>{test.usedAttempts ?? 0} used</span>
                                  {test.remainingAttempts !== null && (
                                    <>
                                      <span className="dot">·</span>
                                      <span
                                        className={
                                          test.remainingAttempts === 0
                                            ? "none-left"
                                            : ""
                                        }
                                      >
                                        {test.remainingAttempts} left
                                      </span>
                                    </>
                                  )}
                                </span>
                                {!test.canAttempt && (
                                  <span className="attempt-badge exhausted">
                                    No attempts left
                                  </span>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Action */}
                          <td className="assessment-table__action">
                            <div className="assessment-table__actions">
                              {status.class === "expired" ? (
                                <IconButton
                                  variant="danger"
                                  icon="material-symbols:error-outline-rounded"
                                  disabled
                                  title="Assessment Expired"
                                  className="assessment-action-btn"
                                />
                              ) : !startable ? (
                                <IconButton
                                  variant="warning"
                                  icon="material-symbols:block-rounded"
                                  disabled
                                  title="No Attempts Left"
                                  className="assessment-action-btn"
                                />
                              ) : (
                                <IconButton
                                  variant="primary"
                                  icon="material-symbols:play-arrow-rounded"
                                  onClick={() => handleStartTest(test.testId)}
                                  title="Start Assessment"
                                  className="assessment-action-btn"
                                />
                              )}
                              {hasResults && (
                                <IconButton
                                  variant="secondary"
                                  icon="material-symbols:visibility-outline-rounded"
                                  onClick={() => handleViewResults(test)}
                                  disabled={resultLoadingTestId === test.testId}
                                  title="View Results"
                                  className="assessment-action-btn"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                /* ── GRID / CARD VIEW ── */
                <div className="tests-grid">
                  {sortedTests.map((test) => {
                    const status = getStatusBadge(test);
                    const isExpired = status.class === "expired";
                    const startable = canStartTest(test);
                    const typeMeta = getAttemptTypeMeta(test);
                    const hasResults = completedTestIds.has(
                      String(test.testId),
                    );

                    return (
                      <div
                        key={test.testId}
                        className={`discovery-test-card discovery-test-card--${typeMeta.key} ${
                          isExpired ? "expired" : ""
                        } ${!startable && !isExpired ? "attempts-exhausted" : ""}`}
                      >
                        <div className="test-card-header">
                          <div
                            className={`test-icon test-icon--${typeMeta.key}`}
                          >
                            <Brain size={24} />
                          </div>
                          <div className="test-badges">
                            <span className={`status-badge ${status.class}`}>
                              {status.label}
                            </span>
                            <span
                              className={`attempt-type-badge attempt-type-badge--${typeMeta.key}`}
                            >
                              {typeMeta.label}
                            </span>
                          </div>
                        </div>

                        <div className="test-card-content">
                          <h4 className="test-title">{test.testTitle}</h4>
                          <p className="test-description">
                            {test.description ||
                              "Comprehensive assessment to evaluate your personality traits and characteristics."}
                          </p>

                          <div className="test-meta">
                            <div className="meta-item">
                              <Calendar size={16} />
                              <span>
                                Expires: {formatDate(test.linkExpiryDate)}
                              </span>
                            </div>
                            {renderAttemptDetails(test)}
                          </div>
                        </div>

                        <div
                          className={`test-card-footer ${
                            !startable ? "disabled-footer" : ""
                          }`}
                        >
                          <div className="test-card-footer__actions">
                            {isExpired ? (
                              <ButtonWithIcon
                                icon="material-symbols:error"
                                text="Expired"
                                onClick={() => {}}
                                className="expired-btn"
                                disabled
                              />
                            ) : !startable ? (
                              <ButtonWithIcon
                                icon="material-symbols:block"
                                text="No Attempts Left"
                                onClick={() => {}}
                                className="no-attempts-btn"
                                disabled
                              />
                            ) : (
                              <ButtonWithIcon
                                icon="material-symbols:play-arrow"
                                text="Start Assessment"
                                onClick={() => handleStartTest(test.testId)}
                                className="start-test-btn"
                              />
                            )}
                            {hasResults && (
                              <ButtonWithIcon
                                icon={
                                  resultLoadingTestId === test.testId
                                    ? "line-md:loading-twotone-loop"
                                    : "material-symbols:visibility-outline-rounded"
                                }
                                text={
                                  resultLoadingTestId === test.testId
                                    ? "Loading…"
                                    : "View Results"
                                }
                                onClick={() => handleViewResults(test)}
                                className="view-results-btn"
                                disabled={resultLoadingTestId === test.testId}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="empty-state">
                <Brain size={64} />
                <h3>
                  {activeTests.length === 0
                    ? activeTab === "assigned"
                      ? "No Assessments Assigned to You"
                      : activeTab === "own"
                        ? "No Assessments Available"
                        : activeTab === "explore"
                          ? "No Featured Assessments"
                          : "No Assessments Found"
                    : "No Assessments Found"}
                </h3>
                <p>
                  {activeTests.length === 0 && activeTab === "assigned"
                    ? "You don't have any assessments assigned to you right now."
                    : activeTests.length === 0 && activeTab === "own"
                      ? "There are no published assessments to take right now."
                      : activeTests.length === 0 && activeTab === "explore"
                        ? "There are no admin-created assessments to explore right now."
                        : "Try adjusting your search or filter criteria"}
                </p>
                {activeTab !== "explore" && (
                  <button
                    type="button"
                    className="empty-state-explore-btn"
                    onClick={handleExploreTab}
                  >
                    <Sparkles size={16} aria-hidden="true" />
                    Try Explore tests instead
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={`discovery-sidebar-wrapper ${
            sidebarCollapsed ? "collapsed" : ""
          }`}
        >
          <button
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? (
              <ChevronLeft size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          {!sidebarCollapsed && (
            <aside className="discovery-sidebar">
              <div className="create-test-card">
                <div className="create-test-icon">
                  <FilePlus2 size={20} />
                </div>
                <h4>Create Your Own Test</h4>
                <p>Build a custom assessment tailored to your needs.</p>
                <button className="create-test-btn" onClick={handleCreateTest}>
                  Create Test
                </button>
              </div>

              <div className="quick-tips-card">
                <button
                  className="quick-tips-header"
                  onClick={() => setTipsCollapsed((prev) => !prev)}
                  aria-expanded={!tipsCollapsed}
                >
                  <span className="quick-tips-title">
                    <Lightbulb size={16} />
                    Quick Tips
                  </span>
                  {tipsCollapsed ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronUp size={16} />
                  )}
                </button>

                {!tipsCollapsed && (
                  <ul className="quick-tips-list">
                    {QUICK_TIPS.map((tip, index) => {
                      const TipIcon = tip.icon;
                      return (
                        <li key={index}>
                          <span className="quick-tip-icon">
                            <TipIcon size={14} />
                          </span>
                          <span>{tip.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      {resultAttemptsPopup && (
        <div
          className="attempt-results-overlay"
          onClick={() => setResultAttemptsPopup(null)}
        >
          <section
            className="attempt-results-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attempt-results-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="attempt-results-popup__header">
              <div>
                <h2 id="attempt-results-title">Choose an Attempt to View</h2>
                <p>{resultAttemptsPopup.test.testTitle || "Assessment"}</p>
              </div>
              <button
                type="button"
                className="attempt-results-popup__close"
                onClick={() => setResultAttemptsPopup(null)}
                aria-label="Close attempts"
              >
                <X size={18} />
              </button>
            </header>

            <div className="attempt-results-popup__list">
              {resultAttemptsPopup.attempts.map((attempt, index) => (
                <div
                  key={attempt.resultId ?? `${attempt.attemptNumber}-${index}`}
                  className="attempt-results-popup__item"
                >
                  <div className="attempt-results-popup__score">
                    <Award size={18} />
                    <div>
                      <strong>
                        Attempt {attempt.attemptNumber ?? index + 1}
                      </strong>
                      <span>
                        Score: {attempt.totalScore ?? "—"}
                        {attempt.totalTestScore
                          ? ` / ${attempt.totalTestScore}`
                          : ""}
                      </span>
                    </div>
                  </div>
                  <div className="attempt-results-popup__action">
                    <span>{formatAttemptDate(attempt.endTime)}</span>
                    <ButtonWithIcon
                      icon="material-symbols:visibility-outline-rounded"
                      text="View Results"
                      onClick={() =>
                        openAttemptResults(resultAttemptsPopup.test, attempt)
                      }
                      className="attempt-results-popup__view"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default DiscoverMoreTests;
